// src/components/Settings.jsx
import React, { useEffect, useState } from "react";
import "./halal-theme.css";
import "./Settings.css";

import { useSettings } from "../contexts/SettingsProvider";
import { useAuth } from "../contexts/SupabaseAuthProvider";
import { supabase } from "../supabaseClient";

import { TOTP, Secret } from "otpauth";
import QRCode from "qrcode";

export default function Settings() {
  const { settings, setSettings } = useSettings();
  const { user } = useAuth();

  // 2FA states
  const [qrImg, setQrImg] = useState(null);
  const [secret, setSecret] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMsg, setOtpMsg] = useState("");

  const [loadingGen, setLoadingGen] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingDisable, setLoadingDisable] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showWarn, setShowWarn] = useState(false);

  const [confirmDisableAuthOpen, setConfirmDisableAuthOpen] = useState(false);
  const [confirmDisableAccOpen, setConfirmDisableAccOpen] = useState(false);

  // Halal Theme Sync
  useEffect(() => {
    if (settings.halal)
      document.documentElement.classList.add("halal-theme");
    else
      document.documentElement.classList.remove("halal-theme");
  }, [settings.halal]);

  // Load user settings from Supabase on first open
  useEffect(() => {
    if (!user?.id) return;

    (async () => {
      const { data } = await supabase
        .from("users_data")
        .select("settings")
        .eq("user_id", user.id)
        .single();

      if (data?.settings) {
        setSettings(data.settings);
      }
    })();
  }, [user?.id]);

  // Timezone
  const tzList =
    Intl.supportedValuesOf?.("timeZone") ||
    [settings.timezone || "UTC"];

  const changeTimezone = async (tz) => {
    const newSettings = { ...settings, timezone: tz };
    setSettings(newSettings);

    await supabase
      .from("users_data")
      .update({ settings: newSettings })
      .eq("user_id", user.id);
  };

  // Disable account for 48h
  const disableAccount = async () => {
    const until = Date.now() + 48 * 60 * 60 * 1000;
    const newSettings = { ...settings, disabledUntil: until };
    setSettings(newSettings);

    await supabase
      .from("users_data")
      .update({ settings: newSettings })
      .eq("user_id", user.id);

    setShowWarn(true);
    setTimeout(() => setShowWarn(false), 3000);
  };

  const timeLeft = () => {
    if (!settings.disabledUntil) return 0;
    return settings.disabledUntil - Date.now();
  };

  const msToHuman = (ms) => {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  };

  // ============================
  // 🔐 Generate QR + Secret
  // ============================
  const generateSecret = async () => {
    if (!user?.email) {
      setOtpMsg("Please login first.");
      return;
    }

    setLoadingGen(true);
    setOtpMsg("");

    try {
      await new Promise((r) => setTimeout(r, 500));

      const sec = new Secret();
      const base32 = sec.base32;
      setSecret(base32);

      const totp = new TOTP({
        issuer: "MyApp Secure",
        label: user.email,
        secret: sec,
      });

      const url = totp.toString();
      const qr = await QRCode.toDataURL(url);
      setQrImg(qr);

    } catch (e) {
      setOtpMsg("Unable to generate QR");
    }

    setLoadingGen(false);
  };

  // ============================
  // 🔐 Verify OTP
  // ============================
  const verifyOtp = async () => {
    if (!secret) return setOtpMsg("Generate QR first");

    setLoadingVerify(true);
    setOtpMsg("");

    try {
      await new Promise((r) => setTimeout(r, 500));

      const totp = new TOTP({
        issuer: "MyApp Secure",
        label: user.email,
        secret: Secret.fromBase32(secret),
      });

      const delta = totp.validate({ token: otp });
      if (delta === null) {
        setOtpMsg("Invalid code");
        setLoadingVerify(false);
        return;
      }

      // Save secure settings in supabase only (not local)
      const newSettings = {
        ...settings,
        authEnabled: true,
        authSecret: secret,
      };

      await supabase
        .from("users_data")
        .update({ settings: newSettings })
        .eq("user_id", user.id);

      setSettings(newSettings);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2800);

    } catch {
      setOtpMsg("Verification failed");
    }

    setLoadingVerify(false);
  };

  // ============================
  // 🚫 Disable Authenticator
  // ============================
  const doDisableAuth = async () => {
    setConfirmDisableAuthOpen(false);
    setLoadingDisable(true);

    try {
      await new Promise((r) => setTimeout(r, 500));

      const newSettings = {
        ...settings,
        authEnabled: false,
        authSecret: "",
      };

      await supabase
        .from("users_data")
        .update({ settings: newSettings })
        .eq("user_id", user.id);

      setSettings(newSettings);
      setQrImg(null);
      setSecret("");
      setOtp("");

      setShowWarn(true);
      setTimeout(() => setShowWarn(false), 2800);

    } catch {
      setOtpMsg("Failed to disable");
    }

    setLoadingDisable(false);
  };

  return (
    <>
      <div className="settings-root">
        <div className="settings-card">
          <h2>Settings</h2>

          {/* =======================
              AUTHENTICATOR 2FA
          ======================== */}
          <section className="settings-section card-anim">
            <h3>Authenticator (2FA)</h3>

            {!settings.authEnabled ? (
              <>
                <button
                  className="btn primary btn-wide"
                  onClick={generateSecret}
                  disabled={loadingGen}
                >
                  {loadingGen ? <div className="spinner"></div> : "Generate QR Code"}
                </button>

                {qrImg && (
                  <div className="qr-wrap">
                    <img src={qrImg} className="qr-img-responsive" alt="QR" />
                  </div>
                )}

                {qrImg && (
                  <>
                    <input
                      className="otp-input"
                      placeholder="Enter 6 digit code"
                      maxLength={6}
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, ""))
                      }
                    />

                    <button
                      className="btn success btn-wide"
                      onClick={verifyOtp}
                      disabled={loadingVerify}
                    >
                      {loadingVerify ? <div className="spinner"></div> : "Verify & Enable"}
                    </button>
                  </>
                )}

                {otpMsg && <p className="muted otp-msg">{otpMsg}</p>}
              </>
            ) : (
              <>
                <p className="muted green">Two-factor authentication is ON</p>

                <button
                  className="btn warn btn-wide"
                  onClick={() => setConfirmDisableAuthOpen(true)}
                >
                  Disable Authenticator
                </button>
              </>
            )}
          </section>

          {/* =======================
              HALAL THEME
          ======================== */}
          <section className="settings-section card-anim">
            <h3>Halal Mode</h3>

            <div className="row">
              <label>Enable Halal Theme</label>
              <button
                className={`toggle ${settings.halal ? "on" : ""}`}
                onClick={() => setSettings({ halal: !settings.halal })}
              >
                {settings.halal ? "ON" : "OFF"}
              </button>
            </div>
          </section>

          {/* =======================
              TIMEZONE
          ======================== */}
          <section className="settings-section card-anim">
            <h3>Timezone</h3>

            <select
              value={settings.timezone}
              onChange={(e) => changeTimezone(e.target.value)}
            >
              {tzList.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </section>

          {/* =======================
              DISABLE ACCOUNT
          ======================== */}
          <section className="settings-section card-anim">
            <h3>Disable Account</h3>

            {settings.disabledUntil > Date.now() ? (
              <p className="danger">
                Disabled — remaining: {msToHuman(timeLeft())}
              </p>
            ) : (
              <button
                className="btn warn btn-wide"
                onClick={() => setConfirmDisableAccOpen(true)}
              >
                Disable for 48 hours
              </button>
            )}
          </section>
        </div>
      </div>

      {/* == SUCCESS POPUP == */}
      {showSuccess && (
        <div className="modal-overlay">
          <div className="modal-card modal-success">
            <div className="modal-icon">✓</div>
            <h3>Authenticator Enabled</h3>
            <p>Your account is now protected.</p>
          </div>
        </div>
      )}

      {/* == WARNING POPUP == */}
      {showWarn && (
        <div className="modal-overlay">
          <div className="modal-card modal-warn">
            <div className="modal-icon">⚠</div>
            <h3>Action Completed</h3>
            <p>Some security restrictions may apply.</p>
          </div>
        </div>
      )}

      {/* == CONFIRM DISABLE AUTH == */}
      {confirmDisableAuthOpen && (
        <div className="modal-overlay">
          <div className="confirm-card">
            <h3>Disable Authenticator?</h3>
            <p className="muted">
              This removes 2FA. Are you sure?
            </p>

            <div className="row modal-actions">
              <button className="btn ghost" onClick={() => setConfirmDisableAuthOpen(false)}>
                Cancel
              </button>
              <button className="btn warn" onClick={doDisableAuth}>
                {loadingDisable ? <div className="spinner"></div> : "Disable Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* == CONFIRM DISABLE ACCOUNT == */}
      {confirmDisableAccOpen && (
        <div className="modal-overlay">
          <div className="confirm-card">
            <h3>Disable Account for 48 hours?</h3>
            <p className="muted">Withdrawals will be blocked. Continue?</p>

            <div className="row modal-actions">
              <button className="btn ghost" onClick={() => setConfirmDisableAccOpen(false)}>
                Cancel
              </button>
              <button className="btn warn" onClick={disableAccount}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
